import * as React from "react";
import _ from "lodash";

import { ActiveCoverSections } from "kw/util/CoverSection/ActiveCoverSections";
import { Block } from "baseui/block";
import { Button } from "kanopi-components/Button";
import { CoverLimitations } from "kw/util/CoverLimitations/CoverLimitations";
import { Currency } from "kanopi-components/Currency";
import { ExposurePrice } from "./ExposurePrice";
import { HeadingSmall } from "kanopi-components/typography";
import { HorizontalSpacer } from "kanopi-components/SpaceWrapper/HorizontalSpacer";
import { InactiveCoverSections } from "kw/util/CoverSection/InactiveCoverSections";
import { Sektion } from "kanopi-components/Layout";
import { StatefulPanel } from "kanopi-components/Accordion";
import { StatelessRiskState } from "kw/util/RiskState";
import { jsonStringToCamelCase } from "kanopi-components/utils/json";
import { useGlobal } from "kanopi-components/useGlobal";
import { useGlobalsContext } from "kw/util/Globals";
import { useStyletron } from "kanopi-components/theme/useStyletron";
import { useUpdateExposureEnabled } from "kw/util/Exposure/useUpdateExposureEnabled";

type ExposureProps = {
  exposureItem?: {
    enabledCovers: Record<string, string>;
    type: string;
    key: string;
    name: string;
    exposure_id: string;
  };
  editable: boolean;
  priceLoading: boolean;
  quote?: TQuote;
  riskProfile: TRiskProfile;
  priceRiskProfile: () => void;
};
export const Exposure: React.FC<ExposureProps> = ({
  editable,
  exposureItem: exposure,
  priceLoading,
  quote,
  riskProfile,
}) => {
  const globalsContext = useGlobalsContext();
  const [, setData] = useGlobal(globalsContext);
  const [, theme] = useStyletron();
  const inactiveCoverSectionsRef = React.createRef<HTMLDivElement>();
  const availableCovers = quote?.product?.exposures?.find(
    (exp) => exp?.type === exposure?.type
  )?.covers;
  const enabledExposureCovers = availableCovers?.filter((cover) =>
    exposure?.enabledCovers?.includes(cover?.key)
  );
  const disabledExposureCovers = availableCovers?.filter((cover) => {
    const enabledExposureKeys = enabledExposureCovers?.map((ec) => ec?.key);
    return (
      cover?.key?.includes(exposure?.key) && !enabledExposureKeys?.includes(cover?.key)
    );
  });
  const productExposures =
    riskProfile?.enabledExposures?.filter(
      (exp) => exp?.product_key === quote?.product?.key
    ) || [];
  const [panelTitle, setPanelTitle] = React.useState<string>(
    productExposures?.length === 1 ? "Hide details" : "View details"
  );
  const { setEnabled } = useUpdateExposureEnabled({
    exposureId: exposure?.exposure_id,
    exposureItemKey: exposure?.key,
  });
  // TODO: DEV-5006. First exposure item of each type is always enabled
  const canRemoveExposure = exposure?.key?.slice(-2) === "_1" ? false : true;

  async function disableExposureItem() {
    await setEnabled(false);
    setData({ reprice: true });
  }

  const pricingOthers = jsonStringToCamelCase(quote?.pricingOthers);
  const exposure_price = _.get(_.get(pricingOthers, exposure.type), exposure.key);
  const exposure_amount = exposure_price?.postTaxPremium;
  const isPolicyLevelExposure = exposure?.type === "policy";
  async function editExposureItem() {
    await setEnabled(false);
    setData({ reprice: true });
  }

  return (
    <StatefulPanel
      initialState={{
        expanded: false,
      }}
      onChange={({ expanded }) => {
        setPanelTitle(expanded ? "Hide details" : "View details");
      }}
      overrides={{
        Content: {
          style: ({ $theme }) => ({
            backgroundColor: $theme.colors.backgroundTertiary,
            borderRadius: $theme.borders.radius200,
          }),
        },
        Header: {
          style: ({ $theme }) => ({
            backgroundColor: $theme.colors.backgroundTertiary,
            maxWidth: "470px",
            margin: "auto",
            borderRadius: `${$theme.borders.radius200} ${$theme.borders.radius200} 0 0`,
          }),
        },
        ToggleIcon: {
          style: {
            display: "none",
          },
        },
      }}
      title={
        <HorizontalSpacer justifyContent="space-between" width="100%">
          <HeadingSmall margin={0}>{exposure?.name}</HeadingSmall>
          {!isPolicyLevelExposure && (
            <ExposurePrice
              periodicPrice={exposure_amount}
              paymentFrequency={quote?.paymentFrequency}
              loading={priceLoading}
              pricingFailed={false}
              productKey={quote?.product?.key}
            ></ExposurePrice>
          )}
          <Button
            size={Button.SIZE.mini}
            kind={Button.KIND.primary}
            shape={Button.SHAPE.pill}
          >
            {panelTitle}
          </Button>
        </HorizontalSpacer>
      }
    >
      <Sektion compact>
        {editable && (
          <HeadingSmall marginTop={0} center>
            <HorizontalSpacer justifyContent="center">
              <Block
                display="flex"
                justifyContent="center"
                paddingTop={theme.sizing.scale600}
              >
                <Button
                  onClick={() => {
                    editExposureItem();
                  }}
                  size={Button.SIZE.mini}
                  shape={Button.SHAPE.pill}
                  kind={Button.KIND.primary}
                  overrides={{
                    BaseButton: { style: { height: "min-content" } },
                  }}
                >
                  {`Edit ${exposure?.type}`}
                </Button>
              </Block>
              {canRemoveExposure && (
                <Block
                  display="flex"
                  justifyContent="center"
                  paddingTop={theme.sizing.scale600}
                >
                  <Button
                    onClick={() => {
                      disableExposureItem();
                    }}
                    size={Button.SIZE.mini}
                    shape={Button.SHAPE.pill}
                    kind={Button.KIND.secondary}
                    overrides={{
                      BaseButton: { style: { height: "min-content" } },
                    }}
                  >
                    {`Remove ${exposure?.type}`}
                  </Button>
                </Block>
              )}
            </HorizontalSpacer>
          </HeadingSmall>
        )}
        <HeadingSmall marginTop={0} center>
          Insured details
        </HeadingSmall>
        <StatelessRiskState
          riskData={riskProfile?.riskData}
          questionSets={quote?.product?.exposures
            ?.find((exp) => exp?.type === exposure?.type)
            ?.exposureQuestionSets?.filter(
              (eqs) => eqs?.exposureItemKey === exposure?.key
            )
            ?.map((eqs) => eqs?.questionSet)}
          disabled={true}
        />
        {!!enabledExposureCovers?.length && (
          <>
            <HeadingSmall center>Cover included</HeadingSmall>
            <ActiveCoverSections
              inactiveSectionRef={inactiveCoverSectionsRef}
              coverSections={enabledExposureCovers}
              disabled={priceLoading}
              prefix="/quote/ready"
              riskProfile={riskProfile}
              allowAdd={false}
              editable={editable}
            />
          </>
        )}
        {editable && !!disabledExposureCovers?.length && (
          <>
            <HeadingSmall center>Add a cover</HeadingSmall>
            <InactiveCoverSections
              coverSections={disabledExposureCovers}
              prefix="/quote/ready"
              riskProfile={riskProfile}
              disabled={priceLoading}
            />
          </>
        )}
        <Block data-testid={quote?.product?.key + "-coverLimitations"}>
          <CoverLimitations
            limitations={quote?.limitations?.filter(
              (limitation) =>
                enabledExposureCovers?.findIndex(
                  (expo) => expo?.key === limitation?.cover?.key
                ) > -1
            )}
          />
        </Block>
        {!isPolicyLevelExposure && (
          <>
            <HeadingSmall marginBottom="5px" center>
              Price breakdown
            </HeadingSmall>
            <Block display="flex" justifyContent="center">
              <table style={{ borderSpacing: "10px" }}>
                <tr>
                  <td align="right">Pre-tax premium</td>
                  <td>
                    <Currency value={exposure_price?.preTaxPremium} />
                  </td>
                </tr>
                <tr>
                  <td align="right">Commission</td>
                  <td>
                    <Currency value={exposure_price?.commission} />
                  </td>
                </tr>
                {exposure?.type !== "policy" && (
                  <>
                    <tr>
                      <td align="right">GST</td>
                      <td>
                        <Currency
                          value={exposure_price?.installments[0].fees[0].amount}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td align="right">SD</td>
                      <td>
                        <Currency
                          value={exposure_price?.installments[0].fees[1].amount}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td align="right">FSL</td>
                      <td>
                        <Currency
                          value={exposure_price?.installments[0].fees[2].amount}
                        />
                      </td>
                    </tr>
                  </>
                )}
                <tr>
                  <td align="right">Post-tax premium</td>
                  <td>
                    <Currency value={exposure_price?.postTaxPremium} />
                  </td>
                </tr>
              </table>
            </Block>
          </>
        )}
      </Sektion>
    </StatefulPanel>
  );
};
